/**
 * Andar Bahar — the game page. PHASE A: standalone, practice wallet, a LOCAL
 * table clock (swap for ServerTableClock in Phase C — one import). Every
 * phase derives from the clock's round; the page only plays it.
 *
 * ONE decision per round: ANDAR or BAHAR. Gold ring = the side you chose.
 * All shown multipliers are TOTAL RETURN (X1.90 / X2.00).
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { type Card, isRed } from '../_cards/deck';
import { PIP_DEFS, faceSvg, backSvg } from '../_cards/art';
import { type Side, payoutFor, otherSide, fmtX } from './engine';
import { ServerTableClock as LocalTableClock, type TableClock, type Round, type Wallet, WINDOW_MS } from './serverTableClock';
import { crowdFor, seatsFor, mask } from './fakes';
import { FELT_SVG, SHOE_SVG, ARROW_SVG, ARROW_SVG_L } from './art';
import {
  initAbSound, isAbSoundOn, setAbSoundOn, unlockAbSound, initAbMusic, isAbMusicOn, setAbMusicOn,
  startAbMusic, stopAbMusic, bindAbVisibility, abBedPhase,
  sJoker, sCountTick, sKnock, sDealCard, sStamp, sWin, sLoss, sSatOut, sChip, sUndo, sDenied, sTap, sCrowd,
} from './sound';
import './andar.css';

const armed = new WeakMap<Element, number>();
function press(fn: () => void) {
  return {
    onPointerDown: (e: React.PointerEvent) => { e.stopPropagation(); armed.set(e.currentTarget, e.pointerId); },
    onPointerUp: (e: React.PointerEvent) => { e.stopPropagation(); if (armed.get(e.currentTarget) !== e.pointerId) return; armed.delete(e.currentTarget); if ((e.currentTarget as HTMLButtonElement).disabled) return; fn(); },
    onPointerCancel: (e: React.PointerEvent) => { armed.delete(e.currentTarget); },
    onKeyDown: (e: React.KeyboardEvent) => { if (e.key !== 'Enter' && e.key !== ' ') return; if (e.repeat) return; e.preventDefault(); e.stopPropagation(); if ((e.currentTarget as HTMLButtonElement).disabled) return; fn(); },
  };
}

// Opening balance BEFORE the server has spoken. It used to be 10,000 — the Phase-A practice
// bankroll — which meant a real-money table showed the player ₹10,000 they did not have until
// the first `/state` landed, and `bet()` gated affordability on that fiction. Zero plus the
// `walletReady` gate below means nothing is ever shown or spent against a number we invented.
// (2026-09-06; the practice clock funds itself, so it is unaffected.)
const PRACTICE_START = 0;
const CHIPS = [10, 50, 100, 500, 1000, 5000];
const MAX_SIDE = 5000;
const inr = (n: number) => `₹${n.toLocaleString('en-IN')}`;
const lsGet = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const lsSet = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* private */ } };
const SIDE_NAME: Record<Side, string> = { andar: 'ANDAR', bahar: 'BAHAR' };

type Strip = { text: string; cls: string };

/** a card: back + face stacked; flips via the host classes */
const CardEl = ({ card, cls, style }: { card: Card; cls: string; style?: React.CSSProperties }) => (
  <div className={`ab-card ${cls}`} style={style}>
    <span className="ab-face--back" dangerouslySetInnerHTML={{ __html: backSvg() }} />
    <span className="ab-face--front" dangerouslySetInnerHTML={{ __html: faceSvg(card.rank, card.suit) }} />
    <span className="ab-face--edge"><i /></span>
    <span className="ab-cring" />
  </div>
);

const AbPage = () => {
  const appRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const shoeRef = useRef<HTMLDivElement>(null);
  const holderRef = useRef<HTMLDivElement>(null);
  const laneRefs = useRef<Record<Side, HTMLDivElement | null>>({ andar: null, bahar: null });
  const balRef = useRef<HTMLSpanElement>(null);

  const balanceRef = useRef(PRACTICE_START);
  const [balance, setBalance] = useState(PRACTICE_START);
  const [balFx, setBalFx] = useState('');
  const wallet = useMemo<Wallet>(() => ({
    balance: () => balanceRef.current,
    debit: (n) => { if (balanceRef.current < n) return false; balanceRef.current -= n; setBalance(balanceRef.current); setBalFx('dip'); window.setTimeout(() => setBalFx(''), 300); return true; },
    credit: (n) => { balanceRef.current += n; setBalance(balanceRef.current); },
  }), []);

  const clockRef = useRef<TableClock | null>(null);
  const [tick, setTick] = useState(0);
  // Re-read every tick: the clock flips this the moment its first /state (or a bet ack) lands.
  const walletReady = clockRef.current?.walletReady() ?? false;
  const [chip, setChip] = useState(() => { const v = Number(lsGet('ab.chip')); return CHIPS.includes(v) ? v : 100; });
  const [lastBet, setLastBet] = useState<{ side: Side; amount: number } | null>(() => { try { const v = JSON.parse(lsGet('ab.last') || 'null'); return v && v.side && v.amount ? v : null; } catch { return null; } });
  const [soundOn, setSoundOn] = useState(initAbSound);
  const [musicOn, setMusicOn] = useState(initAbMusic);
  const [event, setEvent] = useState<(Strip & { until: number }) | null>(null);
  const [countdown, setCountdown] = useState(12);
  const [hint, setHint] = useState(() => lsGet('ab.seen') !== '1');
  const [rulesOpen, setRulesOpen] = useState(false);
  const [coins, setCoins] = useState<{ id: number; cx: number; cy: number }[]>([]);
  const [rebetBlink, setRebetBlink] = useState(false);
  const [holderCls, setHolderCls] = useState('');
  const [jokerCls, setJokerCls] = useState('');
  const [stampOut, setStampOut] = useState(false);
  const [sweeping, setSweeping] = useState(false);
  const [swapX, setSwapX] = useState(0);

  const prevStatusRef = useRef<Round['status'] | 'boot'>('boot');
  const dealtCountRef = useRef(0);
  const timersRef = useRef<number[]>([]);
  const later = useCallback((fn: () => void, ms: number) => { timersRef.current.push(window.setTimeout(fn, ms)); }, []);
  const flyRef = useRef<Map<number, { fx: number; fy: number }>>(new Map());
  const revealedCrowdRef = useRef(0);
  const [crowdRows, setCrowdRows] = useState<string[]>([]);

  useLayoutEffect(() => {
    const el = appRef.current; if (!el) return;
    const write = (w: number, h: number) => { el.style.setProperty('--ab-w', `${Math.round(w)}px`); el.style.setProperty('--ab-h', `${Math.round(h)}px`); };
    const cs = getComputedStyle(el);
    write(el.clientWidth, el.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom));
    const ro = new ResizeObserver((es) => { const r = es[es.length - 1].contentRect; write(r.width, r.height); });
    ro.observe(el, { box: 'content-box' });
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    let seed = Number(lsGet('ab_table_seed'));
    if (!seed) { seed = (Math.random() * 0xffffffff) >>> 0; lsSet('ab_table_seed', String(seed)); }
    const clock = new LocalTableClock(seed, wallet, 120 + (seed % 80));
    clockRef.current = clock;
    const unsub = clock.subscribe(() => setTick((t) => t + 1));
    setTick((t) => t + 1);                                  // the first render must see the round
    if (isAbMusicOn() && isAbSoundOn()) startAbMusic();
    const unbind = bindAbVisibility();
    return () => { unsub(); clock.destroy(); unbind(); stopAbMusic(); timersRef.current.forEach(window.clearTimeout); };
  }, [wallet]);

  const clock = clockRef.current;
  const r = clock?.round();
  const st = r?.status ?? 'betting';
  const bets = clock?.myBets() ?? [];
  const mySide: Side | null = bets.length ? bets[0].side : null;
  const myTotal = bets.reduce((s, b) => s + b.amount, 0);
  const outcome = clock?.myOutcome() ?? null;

  const showEvent = useCallback((text: string, cls: string, hold = 1500) => {
    setEvent({ text, cls, until: performance.now() + hold });
    later(() => setEvent((e) => (e && performance.now() >= e.until - 5 ? null : e)), hold + 10);
  }, [later]);

  /* geometry for flights: shoe → holder / lane slot, in px */
  const flightFrom = (target: HTMLElement | null): { fx: number; fy: number } => {
    const s = shoeRef.current; if (!s || !target) return { fx: 0, fy: 0 };
    const sr = s.getBoundingClientRect(), tr = target.getBoundingClientRect();
    return { fx: sr.left + sr.width * 0.15 - tr.left, fy: sr.top + sr.height * 0.35 - tr.top };
  };

  /* ── phase transitions ─────────────────────────────────────────────── */
  useEffect(() => {
    if (!clock || !r) return;
    if (st === prevStatusRef.current) return;
    const prev = prevStatusRef.current;
    prevStatusRef.current = st;
    if (st === 'betting') {
      dealtCountRef.current = 0; flyRef.current.clear();
      setSweeping(false); setStampOut(false);
      revealedCrowdRef.current = 0; setCrowdRows([]);
      const f = flightFrom(holderRef.current);
      holderRef.current?.style.setProperty('--ab-fx', `${f.fx.toFixed(0)}px`);
      holderRef.current?.style.setProperty('--ab-fy', `${f.fy.toFixed(0)}px`);
      setJokerCls('enter flip'); setHolderCls('');
      if (prev !== 'boot') sJoker();
      later(() => { setJokerCls('faceup'); setHolderCls('pulse'); setSwapX((k) => k + 1); }, 640);
      later(() => setHolderCls('live'), 1840);
      abBedPhase('betting');
      setEvent(null);
    }
    if (st === 'locked') { sKnock(); abBedPhase('dealing', 0); }
    if (st === 'result' && r.winner) {
      later(() => { sStamp(); }, 250);
      later(() => {
        if (!outcome || outcome.staked === 0) sSatOut();
        else if (outcome.returned > 0) { sWin(outcome.returned); setBalFx('up'); later(() => setBalFx(''), 900); fireCoins(r.winner!); }
        else sLoss();
      }, 550);
      abBedPhase('result');
      if (lastBet == null && bets.length) { setRebetBlink(true); later(() => setRebetBlink(false), 1000); }
      if (bets.length) { const lb = { side: bets[0].side, amount: myTotal }; setLastBet(lb); lsSet('ab.last', JSON.stringify(lb)); }
    }
    if (st === 'intermission') {
      abBedPhase('intermission');
      later(() => setSweeping(true), 100);
      later(() => setStampOut(true), 2700);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [st, r?.no]);

  /* dealt cards: sound each new card; flag the lane about to receive */
  useEffect(() => {
    if (!r || st !== 'dealing') return;
    const n = r.dealt.length;
    if (n > dealtCountRef.current) {
      dealtCountRef.current = n;
      const side = n % 2 === 1 ? r.firstSide : otherSide(r.firstSide);
      sDealCard(n, side);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r?.dealt.length, st]);

  /* countdown + ticks + crowd during betting */
  useEffect(() => {
    if (st !== 'betting' || !r) return;
    let lastWhole = -1;
    const tick = () => {
      const left = Math.max(0, Math.ceil((r.locksAt - performance.now()) / 1000));
      setCountdown(left);
      if (left !== lastWhole) { lastWhole = left; if (left <= 3 && left >= 1) sCountTick(left); }
      const t = (performance.now() - r.opensAt) / 1000;
      const crowd = crowdFor(r.fakeSeed, r.no);
      while (revealedCrowdRef.current < crowd.length && crowd[revealedCrowdRef.current].at <= t) {
        const f = crowd[revealedCrowdRef.current++];
        setCrowdRows((rows) => [`${f.name} put ${inr(f.stake)} on ${SIDE_NAME[f.side]}`, ...rows].slice(0, 10));
        sCrowd();
      }
    };
    tick();
    const id = window.setInterval(tick, 200);
    return () => window.clearInterval(id);
  }, [st, r]);

  const fireCoins = (winner: Side) => {
    const app = appRef.current, bal = balRef.current, box = document.querySelector(`.ab-side--${winner}`) as HTMLElement | null;
    if (!app || !bal || !box) return;
    const ar = app.getBoundingClientRect(), br = bal.getBoundingClientRect(), xr = box.getBoundingClientRect();
    app.style.setProperty('--ab-wx', `${(br.left - ar.left + br.width / 2).toFixed(0)}px`);
    app.style.setProperty('--ab-wy', `${(br.top - ar.top + br.height / 2).toFixed(0)}px`);
    const id = Date.now();
    setCoins((c) => [...c, { id, cx: xr.left - ar.left + xr.width / 2, cy: xr.top - ar.top + xr.height / 2 }]);
    later(() => setCoins((c) => c.filter((x) => x.id !== id)), 1200);
  };

  /* ── actions ───────────────────────────────────────────────────────── */
  const place = (side: Side) => {
    const c = clockRef.current; if (!c || !r) return;
    unlockAbSound();
    // Until the server has told us the balance we cannot say whether a stake is affordable, so we
    // do not let one be composed — the alternative is a bet the server refuses for a reason the
    // player was never shown. (2026-09-06)
    if (!c.walletReady()) { showEvent('LOADING YOUR BALANCE…', 'err'); sDenied(); return; }
    if (st !== 'betting') { showEvent('TOO LATE · BETS CLOSED', 'err'); sDenied(); return; }
    if (mySide && mySide !== side) {
      /* one side per round: the whole stake moves and the chip is added */
      const total = myTotal + chip;
      if (total > MAX_SIDE) { showEvent(`MAX ${inr(MAX_SIDE)} ON A SIDE`, 'err'); sDenied(); return; }
      if (chip > balanceRef.current) { showEvent('NOT ENOUGH BALANCE · TAP ₹ TO ADD', 'err'); sDenied(); return; }
      c.unbet(); const res = c.bet(side, total);
      // A REFUSED SWITCH MUST SAY SO (2026-09-06). This used to be `if (res.ok)` with no else,
      // so a refusal took the chips off the table in silence — no toast, no sound — and the
      // player was left looking at an empty side wondering what they had done wrong.
      if (res.ok === false) { showEvent(res.reason === 'balance' ? 'NOT ENOUGH BALANCE · TAP ₹ TO ADD' : 'TOO LATE · BETS CLOSED', 'err'); sDenied(); }
      else { sChip(); showEvent(`${inr(total)} MOVED TO ${SIDE_NAME[side]} ✓ · ${countdown}s`, 'set', 1200); }
      return;
    }
    if (myTotal + chip > MAX_SIDE) { showEvent(`MAX ${inr(MAX_SIDE)} ON A SIDE`, 'err'); sDenied(); return; }
    const res = c.bet(side, chip);                                   // PHASE-2 SEAM
    if (res.ok === false) { showEvent(res.reason === 'balance' ? 'NOT ENOUGH BALANCE · TAP ₹ TO ADD' : 'TOO LATE · BETS CLOSED', 'err'); sDenied(); return; }
    sChip(); navigator.vibrate?.(8);
    if (hint) { setHint(false); lsSet('ab.seen', '1'); }
  };
  const undoAll = () => {
    const c = clockRef.current; if (!c) return;
    const n = c.unbet();                                            // PHASE-2 SEAM: /unbet (one-shot per round)
    if (n > 0) { sUndo(); showEvent(`${inr(n)} BACK IN WALLET`, 'set', 1200); }
  };
  const rebet = () => {
    const c = clockRef.current; if (!c || !lastBet || st !== 'betting' || myTotal > 0) return;
    if (!c.walletReady()) { showEvent('LOADING YOUR BALANCE…', 'err'); sDenied(); return; }
    const res = c.bet(lastBet.side, lastBet.amount);
    if (res.ok === false) { showEvent('NOT ENOUGH BALANCE · TAP ₹ TO ADD', 'err'); sDenied(); return; }
    sChip();
  };

  /* ── the strip ─────────────────────────────────────────────────────── */
  const strip: Strip = (() => {
    if (event && performance.now() < event.until) return event;
    const hurry = countdown <= 3 ? ' hurry' : '';
    switch (st) {
      case 'betting': return mySide ? { text: `${inr(myTotal)} ON ${SIDE_NAME[mySide]} ✓ · ${countdown}s`, cls: 'set' + hurry } : { text: `PICK ANDAR OR BAHAR · ${countdown}s`, cls: 'pick' + hurry };
      case 'locked': return { text: 'BETS CLOSED — DEALING', cls: 'closed' };
      case 'dealing': return r && r.dealt.length ? { text: `CARD ${r.dealt.length}…`, cls: 'deal' } : { text: 'BETS CLOSED — DEALING', cls: 'closed' };
      case 'result': {
        const w = r?.winner ? SIDE_NAME[r.winner] : '';
        if (!outcome || outcome.staked === 0) return { text: `${w} WINS · YOU SAT OUT`, cls: 'out' };
        return outcome.returned > 0 ? { text: `${w} WINS · YOU WON ${inr(outcome.returned)}`, cls: 'win' } : { text: `${w} WINS · YOU LOST ${inr(outcome.staked)}`, cls: 'lose' };
      }
      case 'intermission': return { text: `NEXT ROUND IN ${Math.max(1, Math.ceil(((r?.nextOpensAt ?? 0) - performance.now()) / 1000))}s`, cls: 'next' };
    }
  })();

  const joker = r?.joker;
  const first = r?.firstSide ?? 'andar';
  const payA = joker ? payoutFor('andar', joker) : 1.9, payB = joker ? payoutFor('bahar', joker) : 2.0;
  const seats = r ? seatsFor(r.fakeSeed, r.no) : 120;
  const pot = r ? crowdFor(r.fakeSeed, r.no).reduce((s, f) => s + f.stake, 0) + myTotal : 0;
  const hist = clock?.history().slice(0, 12).reverse() ?? [];
  const betting = st === 'betting';
  const isResult = st === 'result' || st === 'intermission';
  const winner = r?.winner ?? null;
  const dealt = r?.dealt ?? [];
  const cap = 5;
  const laneCards = (side: Side) => dealt.map((c, i) => ({ c, i, side: i % 2 === 0 ? first : otherSide(first) })).filter((x) => x.side === side).slice(-cap);
  /* card geometry in px (mirrors andar.css): lanes stack outward from the centre */
  const W = appRef.current?.clientWidth ?? 360;
  const PAD = 0.012 * W, CARD_W = 0.16 * W, STEP = 0.38 * CARD_W, LANE_W = 0.45 * W;
  const cardLeft = (side: Side, slot: number) => (side === 'andar' ? LANE_W - PAD - CARD_W - slot * STEP : PAD + slot * STEP);
  const sideCls = (side: Side) => {
    const mine = mySide === side;
    if (isResult && winner) return winner === side ? 'won' : 'lost';
    if (st === 'locked' || st === 'dealing') return 'dim' + (mine ? ' mine' : '');
    return (mine ? 'mine' : '') + (betting && countdown <= 3 ? ' hurry' : '');
  };
  const pill = (side: Side) => {
    if (mySide !== side || myTotal === 0) return { cls: '', text: '' };
    if (isResult && outcome) return outcome.returned > 0 ? { cls: 'on win', text: `+${inr(outcome.returned)}` } : { cls: 'on lose', text: `−${inr(outcome.staked)}` };
    return { cls: 'on', text: inr(myTotal) };
  };

  return (
    <div className="ab-root fixed inset-0 z-[9999]">
    <div ref={appRef} className={`ab-app ${dealt.length > 10 ? 'fast' : ''}`} onPointerDown={unlockAbSound}>
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden dangerouslySetInnerHTML={{ __html: `<defs>${PIP_DEFS}</defs>` }} />

      <div className="ab-ribbon">
        <div className="ab-title"><b>ANDAR BAHAR</b><span className="ab-live"><i />LIVE · ROUND #{r?.no ?? 0}</span></div>
        <button type="button" className={`ab-iconbtn ${soundOn ? '' : 'off'}`} aria-label="Sounds" {...press(() => setSoundOn(setAbSoundOn(!isAbSoundOn())))}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden><path d="M4 9v6h4l5 5V4L8 9H4z" /><path d="M16.5 12a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4z" /></svg>
        </button>
        <button type="button" className={`ab-iconbtn ${musicOn ? '' : 'off'}`} aria-label="Music" {...press(() => setMusicOn(setAbMusicOn(!isAbMusicOn())))}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden><path d="M9 17.2V6.3l10-2v10.9h-2V6.7l-6 1.2v9.3z" /><ellipse cx="7" cy="17.6" rx="2.9" ry="2.4" /><ellipse cx="17" cy="15.6" rx="2.9" ry="2.4" /></svg>
        </button>
        <span ref={balRef} className={`ab-bal ab-num ${balFx}`} title={walletReady ? 'Your balance' : 'Loading your balance'}>{walletReady ? inr(balance) : '₹—'}</span>
      </div>

      <div className="ab-hist">
        <em>LAST 12</em>
        {hist.map((h, i) => <span key={`${r?.no}-${i}`} className={`ab-dot ab-dot--${h.winner} ${i === hist.length - 1 && isResult ? 'new' : ''}`}>{h.winner === 'andar' ? 'A' : 'B'}</span>)}
      </div>

      <div ref={stageRef} className="ab-stage">
        <div className="ab-felt" dangerouslySetInnerHTML={{ __html: FELT_SVG }} />
        <div className="ab-abs ab-pot ab-num">POT {inr(pot)} · {seats} PLAYING</div>
        <div ref={shoeRef} className="ab-abs ab-shoe" dangerouslySetInnerHTML={{ __html: SHOE_SVG }} />
        <div className={`ab-abs ab-arrow ab-arrow--${first} ${jokerCls.includes('faceup') ? 'on' : ''}`} dangerouslySetInnerHTML={{ __html: first === 'andar' ? ARROW_SVG_L : ARROW_SVG }} />
        <div ref={holderRef} className={`ab-abs ab-holder ${holderCls} ${joker ? '' : 'empty'}`}>
          <span className="ab-ring" />
          {joker && <CardEl key={r?.no} card={joker} cls={`ab-card--joker ${jokerCls}`} />}
          {betting && <span className="ab-count" key={`c-${r?.no}`}><i /></span>}
        </div>
        <div className={`ab-abs ab-rule ${hint ? 'gold' : ''}`}>{joker ? (isRed(joker) ? 'RED JOKER · BAHAR GETS 1ST CARD' : 'BLACK JOKER · ANDAR GETS 1ST CARD') : ''}</div>
        {(['andar', 'bahar'] as Side[]).map((side) => {
          const cards = laneCards(side);
          return (
            <div key={side} ref={(el) => { laneRefs.current[side] = el; }} className={`ab-lane ab-lane--${side}`}>
              <b>{SIDE_NAME[side]}</b>
              <span className="ab-slot0" />
              <span className="ab-chev">{side === 'andar' ? '‹‹' : '››'}</span>
              {cards.map(({ c, i }, slot) => {
                let f = flyRef.current.get(i);
                if (!f) { const g = flightFrom(laneRefs.current[side]); f = { fx: g.fx - cardLeft(side, slot), fy: g.fy - PAD }; flyRef.current.set(i, f); }
                const isMatch = isResult && i === dealt.length - 1;
                const lost = isResult && winner && side !== winner;
                const cls = `ab-card--dealt flip ${sweeping ? 'sweep' : 'deal'} ${isMatch ? 'match' : ''} ${lost ? 'lost' : ''}`;
                return <CardEl key={`${r?.no}-${i}`} card={c} cls={cls} style={{ zIndex: slot + 1, ['--ab-slot' as string]: slot, ['--ab-fx' as string]: `${f.fx.toFixed(0)}px`, ['--ab-fy' as string]: `${f.fy.toFixed(0)}px`, ['--ab-fr' as string]: side === 'andar' ? '-9deg' : '7deg', ['--ab-si' as string]: dealt.length - 1 - i }} />;
              })}
            </div>
          );
        })}
        {(st === 'locked' || st === 'dealing') && <div className="ab-abs ab-closed">BETS CLOSED</div>}
        <div className="ab-boxes">
          {(['andar', 'bahar'] as Side[]).map((side) => (
            <button key={side} type="button" className={`ab-box ab-box--${side} ${sideCls(side)}`} {...press(() => place(side))}>
              <span className="ab-ignite" />
              <b>{SIDE_NAME[side]}</b>
              <i key={`x-${swapX}`} className={swapX ? 'swap ab-num' : 'ab-num'}>{fmtX(side === 'andar' ? payA : payB)}</i>
              {hint && <small>{side === 'andar' ? 'INSIDE · LEFT' : 'OUTSIDE · RIGHT'}</small>}
              <em className={`ab-num ${pill(side).cls}`}>{pill(side).text || '·'}</em>
            </button>
          ))}
        </div>
        {isResult && winner && !stampOut && (
          <div className={`ab-abs ab-stamp ab-stamp--${winner}`}>{SIDE_NAME[winner]} WINS</div>
        )}
        {isResult && winner && stampOut && <div className={`ab-abs ab-stamp ab-stamp--${winner} out`}>{SIDE_NAME[winner]} WINS</div>}
        {st !== 'intermission' && (
          <div className={`ab-abs ab-you ab-num ${mySide ?? ''}`}><b>YOU</b>{mySide ? `${inr(myTotal)} · ${SIDE_NAME[mySide]}` : 'NO BET'}</div>
        )}
      </div>

      <div className={`ab-strip ${strip.cls}`} key={strip.text.replace(/\d+s$/, '')} {...press(() => setRulesOpen(true))}>{strip.text}</div>

      <div className="ab-dock">
        <div className={`ab-rail ${betting ? '' : 'closed'}`}>
          {CHIPS.map((c) => (
            <button key={c} type="button" data-v={c} className={`ab-chip ${chip === c ? 'sel' : ''}`} disabled={!walletReady || c > balance} aria-label={`₹${c} chip`}
                    {...press(() => { setChip(c); lsSet('ab.chip', String(c)); sTap(); })}>
              {c >= 1000 ? `${c / 1000}k` : c}
            </button>
          ))}
        </div>
        <div className="ab-sides">
          {(['andar', 'bahar'] as Side[]).map((side) => (
            <button key={side} type="button" className={`ab-side ab-side--${side} ${sideCls(side)}`} {...press(() => place(side))}>
              <span className="ab-ignite" />
              <b>{SIDE_NAME[side]}</b>
              <i key={`x-${swapX}`} className={`ab-num ${swapX ? 'swap' : ''}`}>{fmtX(side === 'andar' ? payA : payB)}</i>
              <em className={`ab-num ${pill(side).cls}`}>{pill(side).text || '·'}</em>
            </button>
          ))}
        </div>
        <div className="ab-tools">
          <button type="button" className="ab-pill" disabled={!betting || myTotal === 0} {...press(undoAll)}>↶ UNDO</button>
          <span className="ab-total ab-num">TOTAL {inr(myTotal)}</span>
          <button type="button" className={`ab-pill ab-pill--rebet ${rebetBlink ? 'blink' : ''}`} disabled={!walletReady || !lastBet || !betting || myTotal > 0} {...press(rebet)}>
            {lastBet ? `↻ REBET ${inr(lastBet.amount)}` : '↻ REBET'}
          </button>
        </div>
      </div>

      <div className="ab-ticker">
        <div className="ab-ticker-track" key={crowdRows.length ? crowdRows[0] : 'e'}>
          {[...crowdRows, ...crowdRows].map((t, i) => <span key={i} className="ab-ticker-item">{t.split(' put ')[0]} put <b>{t.split(' put ')[1]}</b></span>)}
          {crowdRows.length === 0 && <span className="ab-ticker-item">{seats} playing now · {mask('Meera')} top take today ₹48,000</span>}
        </div>
      </div>

      {coins.length > 0 && (
        <div className="ab-coins" aria-hidden>
          {coins.map((b) => Array.from({ length: 8 }, (_, i) => (
            <span key={`${b.id}-${i}`} className="ab-coin" style={{ ['--ab-cx' as string]: `${b.cx}px`, ['--ab-cy' as string]: `${b.cy}px`, ['--ab-d' as string]: `${i * 45}ms`, ['--ab-jit' as string]: `${(i % 2 ? 1 : -1) * 8}px` }} />
          )))}
        </div>
      )}
      {hint && betting && <div className="ab-hint">👇 TAP A SIDE</div>}
      {rulesOpen && (
        <>
          <div className="ab-backdrop" {...press(() => setRulesOpen(false))} />
          <div className="ab-sheet">
            <i />
            <h3>ANDAR BAHAR — HOW TO PLAY</h3>
            <div>
              1. A JOKER card is shown. You have 12 seconds.<br />2. Pick a chip. Tap ANDAR (left) or BAHAR (right). Tap again to add more.<br />3. Cards are dealt one by one, left and right, until a card has the same number as the JOKER. That side wins.
              <b>FIRST CARD RULE</b>Black joker (♠ ♣) → the first card goes to ANDAR. Red joker (♥ ♦) → the first card goes to BAHAR. The side that gets the first card wins a little more often, so it pays X1.90. The other side pays X2.00. The buttons always show the right numbers for this round.
              <b>WHAT YOU GET BACK</b>Every X is your TOTAL RETURN. ₹100 on X2.00 → ₹200 back. ₹100 on X1.90 → ₹190 back. Lose → your chips are gone. No bet → nothing changes.
              <b>ONE SIDE PER ROUND</b>Tapping the other side moves your whole bet there. UNDO takes back everything you placed this round.
              <b>WHEN THE TIMER HITS 0</b>Bets are closed. Nothing can be added or undone.
              <b>REBET</b>After a round, REBET puts the same money on the same side in one tap.
              <b>FAIR DEAL</b>One 52-card deck, shuffled fresh every round. The Joker is drawn first; the rest are dealt until a match. Everyone at the table sees the same cards.
            </div>
            <button type="button" {...press(() => setRulesOpen(false))}>GOT IT</button>
          </div>
        </>
      )}
    </div>
    </div>
  );
};

export default AbPage;
